import { CSSInterpolation } from '@emotion/serialize'
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  TypographyProps
} from '@mui/material'
import React from 'react'

import log from 'log'
import theme from 'theme'
import { ChildrenFunctionOrNode } from 'types/util'

// <div> with a css prop
type LoadingBoxProps = React.HTMLAttributes<HTMLDivElement> & {
  css: CSSInterpolation
}

type SectionProps = {
  children: ChildrenFunctionOrNode
  isLoading?: boolean
  hasError?: boolean
  isLoadingOverlayFullScreen?: boolean
  errorMessage?: React.ReactNode
  darkTheme?: boolean
  renderError?: () => React.ReactNode
  loadingBoxProps?: LoadingBoxProps
}

type SectionState = { hasError: boolean }

export default class PageSection extends React.Component<
  SectionProps,
  SectionState
> {
  static getDerivedStateFromError(): SectionState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  componentDidCatch(error: Error): void {
    log.error(error)
  }

  state = {
    hasError: false
  }

  render(): React.ReactNode {
    const {
      hasError,
      errorMessage,
      isLoading,
      children,
      darkTheme = false,
      renderError = null,
      isLoadingOverlayFullScreen = true,
      loadingBoxProps = {} as LoadingBoxProps,
      ...rest
    } = this.props

    if (hasError || this.state.hasError) {
      return (
        renderError?.() ?? (
          <ErrorSection darkTheme={darkTheme} message={errorMessage} />
        )
      )
    }

    const { css: loadingBoxCss, ...loadingBoxOtherProps } = loadingBoxProps

    if (isLoading) {
      return (
        <section
          css={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1
          }}
          {...rest}
        >
          <Box
            data-test-loading-indicator
            css={[
              !isLoadingOverlayFullScreen && {
                position: 'relative'
              },
              loadingBoxCss
            ]}
            {...loadingBoxOtherProps}
          >
            <Backdrop
              css={[
                !isLoadingOverlayFullScreen && {
                  position: 'absolute'
                }
              ]}
              invisible={!darkTheme}
              open={isLoading}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          </Box>
        </section>
      )
    }

    // accepts function-as-children so that child content is not evaluated until we're sure the loading state is passed
    return (
      <section
        css={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1
        }}
        {...rest}
      >
        {typeof children === 'function' ? children() : children}
      </section>
    )
  }
}

export const ErrorSection: React.FC<{
  darkTheme?: boolean
  message?: React.ReactNode
  typographyProps?: TypographyProps
}> = ({
  message = "We've encountered an error with this content. Please try again later.",
  darkTheme,
  typographyProps,
  ...rest
}) => {
  return (
    <div
      data-test-error-section
      css={{
        paddingTop: theme.space[5],
        paddingRight: 0,
        paddingBottom: theme.space[5],
        paddingLeft: 0
      }}
      role="alert"
      {...rest}
    >
      <Typography
        color={darkTheme ? 'white' : 'inherit'}
        variant="h5"
        {...typographyProps}
      >
        {message}
      </Typography>
    </div>
  )
}
